import { Box, Button, Flex, Text, Textarea } from '@chakra-ui/react';
import React, { useState } from 'react';

import { AgentV } from '@finley-technologies/finley-types';

import { useAgentVStream, AgentVTurn } from './useAgentVStream';
import {
  FinleyThinkingSpinner,
  PromptChatBubble,
} from '~/packages/finleyAi/ui/FinleyAiThreadComponents';

const BlockView = ({ block }: { block: AgentV.AgentVBlock }) => {
  switch (block.type) {
    case 'text':
      return (
        <Text mt="16px" whiteSpace="pre-line">
          {block.text}
        </Text>
      );
    case 'reasoning':
      return (
        <Text
          mt="16px"
          fontStyle="italic"
          color="gray.500"
          whiteSpace="pre-line"
        >
          {block.summary}
        </Text>
      );
    case 'toolCall':
      return (
        <Text mt="8px" fontSize="14px" color="gray.500">
          🔧 {block.toolName} ({block.status})
        </Text>
      );
    // graphDiff is reserved in the contract but not rendered in step 1.
    default:
      return null;
  }
};

const TurnView = ({ turn }: { turn: AgentVTurn }) => {
  return (
    <Box mb="24px">
      <PromptChatBubble promptString={turn.prompt} />
      {turn.blocks.map((block, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <BlockView key={i} block={block} />
      ))}
    </Box>
  );
};

/**
 * Shared AgentV chat window (step 1). Used by the admin dashboard and the
 * backup-servicing customer portals — they differ only in the `streamUrl`
 * (which finleycore app the SSE route lives on).
 *
 * A dumb consumer of the `AgentV` stream contract: it renders each turn's
 * `AgentVBlock[]` by switching on `block.type`. Only `text` and `reasoning`
 * render today; `toolCall` renders as a minimal status line to prove the tool
 * path works. Richer block renderers (graph / diff) slot into `BlockView`
 * later without touching the transport.
 */
export const AgentVChat = ({
  streamUrl,
  targetOrganizationId,
}: {
  streamUrl: string;
  targetOrganizationId: string;
}) => {
  const { turns, isStreaming, error, model, sendPrompt } = useAgentVStream(
    streamUrl,
    targetOrganizationId,
  );
  const [draft, setDraft] = useState<string>('');

  const onSend = (): void => {
    // Don't clear the draft when there's nothing to send (whitespace-only) —
    // sendPrompt no-ops on it, so clearing would silently drop the user's text.
    if (!draft.trim()) return;
    sendPrompt(draft);
    setDraft('');
  };

  return (
    <Flex direction="column" h="70vh" maxW="900px">
      {model && (
        <Text mb="8px" fontSize="12px" color="gray.500">
          Model: {model}
        </Text>
      )}
      <Box flex="1" overflowY="auto" pr="8px">
        {turns.map((turn, i) => (
          // Turns are append-only and never reordered, so index is a stable key.
          // eslint-disable-next-line react/no-array-index-key
          <TurnView key={i} turn={turn} />
        ))}
        {isStreaming && (
          <Box mt="16px">
            <FinleyThinkingSpinner />
          </Box>
        )}
        {error && (
          <Text mt="16px" color="red.500">
            {error}
          </Text>
        )}
      </Box>

      <Flex mt="16px" gap="8px">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Ask AgentV…"
          rows={2}
          isDisabled={isStreaming}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              onSend();
            }
          }}
        />
        <Button onClick={onSend} isDisabled={isStreaming || !draft.trim()}>
          Send
        </Button>
      </Flex>
    </Flex>
  );
};
